<template>
  <div class="sparnatural-page" :style="{ height: `${iframeHeight}px` }">
    <div v-if="loading" class="sparnatural-loading">
      <div class="spinner-border text-primary" role="status">
        <span aria-label="Loading Sparnatural..." class="visually-hidden">Loading Sparnatural...</span>
      </div>
    </div>
    <div v-if="error" class="sparnatural-error">
      <p class="by-copy-large-regular text-by-neutral-100 mb-4">
        Sparnatural konnte nicht geladen werden.
      </p>
      <div class="d-flex gap-3">
        <button class="by-btn-secondary-large-light" @click="retry">
          Erneut versuchen
        </button>
        <a :href="sparnaturalUrl" target="_blank" rel="noopener noreferrer" class="by-btn-secondary-large-light">
          In neuem Tab öffnen
        </a>
      </div>
    </div>
    <iframe
      v-show="!loading && !error"
      ref="iframeRef"
      :src="sparnaturalUrl"
      title="Sparnatural Query Builder"
      allow="fullscreen"
      referrerpolicy="no-referrer"
      loading="eager"
      class="sparnatural-iframe"
      @load="onLoad"
      @error="onError"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, onMounted, onUnmounted, ref } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { useRuntimeEnv } from '@/composables/useRuntimeEnv';

const SPARNATURAL_DEFAULT_URL = 'https://dev.sparnatural.de/';
const SPINNER_DURATION_MS = 600;
const NO_PAGE_SCROLLBAR_CLASS = 'no-page-scrollbar';

export default defineComponent({
  name: 'Sparnatural',
  setup() {
    const env = useRuntimeEnv();
    const sparnaturalUrl = computed(() => env?.content?.sparnatural?.url || SPARNATURAL_DEFAULT_URL);
    const piveauHeaderHeight = inject('piveauHeaderHeight', ref(0));
    const { height: viewportHeight } = useWindowSize();
    const iframeHeight = computed(() => Math.max(0, viewportHeight.value - piveauHeaderHeight.value));
    const loading = ref(true);
    const error = ref(false);
    const iframeRef = ref<HTMLIFrameElement | null>(null);
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    function clearLoadTimeout(): void {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    }

    function startSpinnerTimeout(): void {
      clearLoadTimeout();
      timeoutId = setTimeout(() => {
        loading.value = false;
      }, SPINNER_DURATION_MS);
    }

    function onLoad(): void {
      clearLoadTimeout();
      loading.value = false;
      error.value = false;
    }

    function onError(): void {
      clearLoadTimeout();
      loading.value = false;
      error.value = true;
    }

    function retry(): void {
      loading.value = true;
      error.value = false;
      if (!iframeRef.value) {
        startSpinnerTimeout();
        return;
      }
      iframeRef.value.src = 'about:blank';
      setTimeout(() => {
        if (iframeRef.value) {
          iframeRef.value.src = sparnaturalUrl.value;
        }
        startSpinnerTimeout();
      }, 0);
    }

    startSpinnerTimeout();
    onMounted(() => document.documentElement.classList.add(NO_PAGE_SCROLLBAR_CLASS));
    onUnmounted(() => {
      clearLoadTimeout();
      document.documentElement.classList.remove(NO_PAGE_SCROLLBAR_CLASS);
    });

    return { sparnaturalUrl, loading, error, iframeRef, iframeHeight, onLoad, onError, retry };
  },
});
</script>

<style>
/* The embedded app owns the only visible scrollbar (.app-scroll inside the
   iframe); the page scrollbar is hidden while this route is active. The page
   still scrolls — scroll chaining from the iframe reveals the footer — but
   without a second scrollbar track next to the app's own. */
html.no-page-scrollbar {
  scrollbar-width: none;
}
html.no-page-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>

<style scoped>
.sparnatural-page {
  width: 100%;
  position: relative;
}

.sparnatural-iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}

.sparnatural-loading,
.sparnatural-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  display: none;
}
</style>
